import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * MarketConstellationBackground
 * 
 * A neon constellation network with S&P 500 ticker pulses:
 * - Floating nodes (dots) that drift slowly
 * - Faint neon connections between nearby nodes
 * - Pulse signals traveling along connections with optional ticker labels
 * - Subtle parallax on mouse move (desktop only)
 * - Respects prefers-reduced-motion
 * - Pauses when not in viewport
 */

// S&P 500 tickers (common, recognizable names)
const SP500_TICKERS = [
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA', 'BRK.B', 'UNH', 'JNJ',
  'V', 'XOM', 'JPM', 'PG', 'MA', 'HD', 'CVX', 'MRK', 'ABBV', 'PEP',
  'KO', 'COST', 'AVGO', 'LLY', 'WMT', 'MCD', 'CSCO', 'ACN', 'TMO', 'ABT',
  'CRM', 'DHR', 'NKE', 'ORCL', 'ADBE', 'NFLX', 'AMD', 'TXN', 'UPS', 'PM',
  'NEE', 'RTX', 'HON', 'QCOM', 'LOW', 'UNP', 'IBM', 'BA', 'SBUX', 'GE',
  'CAT', 'INTU', 'DE', 'AMAT', 'GS', 'BLK', 'ISRG', 'AXP', 'MDLZ', 'ADI',
  'GILD', 'PLD', 'SYK', 'VRTX', 'MMC', 'TJX', 'CVS', 'BKNG', 'ADP', 'C',
  'LRCX', 'MS', 'SCHW', 'ZTS', 'MU', 'NOW', 'PANW', 'SNPS', 'REGN', 'CME',
];

const MarketConstellationBackground = ({ 
  className = '',
  heroTextBounds = null // { x, y, width, height } - area to dim labels
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);
  const pulsesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0, active: false });
  const lastTimeRef = useRef(0);
  const isVisibleRef = useRef(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const frameCountRef = useRef(0);
  const lastPulseTimeRef = useRef(0);
  const dimensionsRef = useRef({ width: 0, height: 0 });

  // Configuration
  const CONFIG = {
    // Node settings
    nodeCountDesktop: 55,
    nodeCountMobile: 28,
    nodeRadius: { min: 1.5, max: 2.5 },
    nodeSpeed: 0.12,
    nodeBrightness: { min: 0.3, max: 0.7 },
    
    // Connection settings
    connectionDistance: 180,
    maxConnectionsPerNode: 3,
    connectionOpacityMultiplier: 0.12,
    
    // Pulse settings
    pulseSpawnIntervalMin: 800,
    pulseSpawnIntervalMax: 1500,
    maxPulsesDesktop: 4,
    maxPulsesMobile: 2,
    pulseDuration: 2000,
    pulseRadius: 3,
    pulseTrailLength: 0.25,
    tickerShowChance: 0.6, // 60% of pulses show ticker
    
    // Ticker label settings
    tickerFontSize: 9,
    tickerOpacityMax: 0.7,
    tickerOffsetY: -12, // Float above the pulse
    tickerFadeInEnd: 0.15,
    tickerFadeOutStart: 0.85,
    
    // Hero text area dimming
    heroAreaDimFactor: 0.3,
    
    // Visual settings
    neonColors: {
      primary: { r: 59, g: 130, b: 246 },    // Blue #3B82F6
      secondary: { r: 0, g: 229, b: 255 },    // Cyan #00E5FF
      accent: { r: 139, g: 92, b: 246 }       // Purple #8B5CF6
    },
    
    // Parallax settings
    parallaxStrength: 0.015,
    
    // Performance settings
    targetFPS: 30,
    neighborUpdateInterval: 30,
  };

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Initialize nodes
  const initNodes = useCallback((width, height) => {
    const isMobile = width < 768;
    const nodeCount = isMobile ? CONFIG.nodeCountMobile : CONFIG.nodeCountDesktop;
    const nodes = [];

    for (let i = 0; i < nodeCount; i++) {
      const colorType = Math.random();
      let color;
      if (colorType < 0.5) {
        color = CONFIG.neonColors.primary;
      } else if (colorType < 0.8) {
        color = CONFIG.neonColors.secondary;
      } else {
        color = CONFIG.neonColors.accent;
      }

      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * CONFIG.nodeSpeed,
        vy: (Math.random() - 0.5) * CONFIG.nodeSpeed,
        radius: CONFIG.nodeRadius.min + Math.random() * (CONFIG.nodeRadius.max - CONFIG.nodeRadius.min),
        brightness: CONFIG.nodeBrightness.min + Math.random() * (CONFIG.nodeBrightness.max - CONFIG.nodeBrightness.min),
        color,
        neighbors: [],
        wanderAngle: Math.random() * Math.PI * 2,
      });
    }

    return nodes;
  }, []);

  // Update neighbor lists
  const updateNeighbors = useCallback((nodes, maxDist) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const neighbors = [];
      
      for (let j = 0; j < nodes.length; j++) {
        if (i === j) continue;
        const other = nodes[j];
        const dx = other.x - node.x;
        const dy = other.y - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < maxDist) {
          neighbors.push({ index: j, distance: dist });
        }
      }
      
      neighbors.sort((a, b) => a.distance - b.distance);
      node.neighbors = neighbors.slice(0, CONFIG.maxConnectionsPerNode);
    }
  }, []);

  // Create a new pulse with optional ticker
  const createPulse = useCallback((nodes) => {
    if (nodes.length < 2) return null;
    
    const validNodes = nodes.filter(n => n.neighbors.length > 0);
    if (validNodes.length === 0) return null;
    
    const startNode = validNodes[Math.floor(Math.random() * validNodes.length)];
    const startIndex = nodes.indexOf(startNode);
    const neighbor = startNode.neighbors[Math.floor(Math.random() * startNode.neighbors.length)];
    
    const endNode = nodes[neighbor.index];
    const mixedColor = {
      r: (startNode.color.r + endNode.color.r) / 2,
      g: (startNode.color.g + endNode.color.g) / 2,
      b: (startNode.color.b + endNode.color.b) / 2,
    };
    
    // Determine if this pulse shows a ticker
    const showTicker = Math.random() < CONFIG.tickerShowChance;
    const ticker = showTicker ? SP500_TICKERS[Math.floor(Math.random() * SP500_TICKERS.length)] : null;
    
    return {
      startIndex,
      endIndex: neighbor.index,
      progress: 0,
      startTime: performance.now(),
      duration: CONFIG.pulseDuration,
      color: mixedColor,
      ticker,
      // Small random offset for ticker label
      tickerOffsetX: (Math.random() - 0.5) * 6,
      tickerOffsetY: CONFIG.tickerOffsetY + (Math.random() - 0.5) * 4,
    };
  }, []);

  // Check if point is in hero text area (for dimming)
  const isInHeroArea = useCallback((x, y, width, height) => {
    // Define hero text area as center portion of canvas
    const centerX = width / 2;
    const centerY = height * 0.45;
    const areaWidth = Math.min(500, width * 0.5);
    const areaHeight = 200;
    
    return (
      x > centerX - areaWidth / 2 &&
      x < centerX + areaWidth / 2 &&
      y > centerY - areaHeight / 2 &&
      y < centerY + areaHeight / 2
    );
  }, []);

  // Main animation setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    let width, height, dpr;

    // Resize handler
    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      dimensionsRef.current = { width, height };
      
      nodesRef.current = initNodes(width, height);
      updateNeighbors(nodesRef.current, CONFIG.connectionDistance);
    };

    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resize, 150);
    };

    resize();
    window.addEventListener('resize', debouncedResize);

    // Mouse move handler
    const handleMouseMove = (e) => {
      if (prefersReducedMotion) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: (e.clientX - rect.left) / width - 0.5,
        y: (e.clientY - rect.top) / height - 0.5,
        active: true,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    if (window.innerWidth >= 768) {
      canvas.parentElement?.addEventListener('mousemove', handleMouseMove);
      canvas.parentElement?.addEventListener('mouseleave', handleMouseLeave);
    }

    // Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        isVisibleRef.current = entries[0].isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    // Animation loop
    const animate = (currentTime) => {
      animationRef.current = requestAnimationFrame(animate);

      if (!isVisibleRef.current) return;

      const deltaTime = currentTime - lastTimeRef.current;
      const targetFrameTime = 1000 / CONFIG.targetFPS;
      if (deltaTime < targetFrameTime) return;
      lastTimeRef.current = currentTime;

      frameCountRef.current++;
      const nodes = nodesRef.current;
      const pulses = pulsesRef.current;
      const isMobile = width < 768;

      // Spawn new pulses
      const pulseInterval = CONFIG.pulseSpawnIntervalMin + 
        Math.random() * (CONFIG.pulseSpawnIntervalMax - CONFIG.pulseSpawnIntervalMin);
      const maxPulses = isMobile ? CONFIG.maxPulsesMobile : CONFIG.maxPulsesDesktop;
      
      if (currentTime - lastPulseTimeRef.current > pulseInterval && pulses.length < maxPulses) {
        const newPulse = createPulse(nodes);
        if (newPulse) {
          pulses.push(newPulse);
          lastPulseTimeRef.current = currentTime;
        }
      }

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Calculate parallax offset
      let offsetX = 0, offsetY = 0;
      if (mouseRef.current.active && !prefersReducedMotion) {
        offsetX = mouseRef.current.x * width * CONFIG.parallaxStrength;
        offsetY = mouseRef.current.y * height * CONFIG.parallaxStrength;
      }

      // Update node positions
      if (!prefersReducedMotion) {
        for (const node of nodes) {
          node.wanderAngle += (Math.random() - 0.5) * 0.08;
          node.vx += Math.cos(node.wanderAngle) * 0.008;
          node.vy += Math.sin(node.wanderAngle) * 0.008;
          
          node.vx *= 0.99;
          node.vy *= 0.99;
          
          const maxSpeed = CONFIG.nodeSpeed * 2;
          const speed = Math.sqrt(node.vx * node.vx + node.vy * node.vy);
          if (speed > maxSpeed) {
            node.vx = (node.vx / speed) * maxSpeed;
            node.vy = (node.vy / speed) * maxSpeed;
          }
          
          node.x += node.vx;
          node.y += node.vy;
          
          const padding = 20;
          if (node.x < padding) { node.x = padding; node.vx *= -1; }
          if (node.x > width - padding) { node.x = width - padding; node.vx *= -1; }
          if (node.y < padding) { node.y = padding; node.vy *= -1; }
          if (node.y > height - padding) { node.y = height - padding; node.vy *= -1; }
        }

        if (frameCountRef.current % CONFIG.neighborUpdateInterval === 0) {
          updateNeighbors(nodes, CONFIG.connectionDistance);
        }
      }

      // Draw connections
      ctx.lineCap = 'round';
      for (const node of nodes) {
        const nx = node.x + offsetX;
        const ny = node.y + offsetY;
        
        for (const neighbor of node.neighbors) {
          const other = nodes[neighbor.index];
          const ox = other.x + offsetX;
          const oy = other.y + offsetY;
          
          const dist = neighbor.distance;
          const opacity = (1 - dist / CONFIG.connectionDistance) * CONFIG.connectionOpacityMultiplier;
          
          // Outer glow
          ctx.strokeStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, ${opacity * 0.3})`;
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(nx, ny);
          ctx.lineTo(ox, oy);
          ctx.stroke();
          
          // Inner line
          ctx.strokeStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, ${opacity})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(nx, ny);
          ctx.lineTo(ox, oy);
          ctx.stroke();
        }
      }

      // Draw pulses with ticker labels
      if (!prefersReducedMotion) {
        const now = performance.now();
        ctx.font = `600 ${CONFIG.tickerFontSize}px Inter, -apple-system, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        for (let i = pulses.length - 1; i >= 0; i--) {
          const pulse = pulses[i];
          const elapsed = now - pulse.startTime;
          pulse.progress = elapsed / pulse.duration;
          
          if (pulse.progress >= 1) {
            pulses.splice(i, 1);
            continue;
          }
          
          const startNode = nodes[pulse.startIndex];
          const endNode = nodes[pulse.endIndex];
          if (!startNode || !endNode) {
            pulses.splice(i, 1);
            continue;
          }
          
          const sx = startNode.x + offsetX;
          const sy = startNode.y + offsetY;
          const ex = endNode.x + offsetX;
          const ey = endNode.y + offsetY;
          
          // Current position
          const px = sx + (ex - sx) * pulse.progress;
          const py = sy + (ey - sy) * pulse.progress;
          
          // Draw trailing glow
          const trailStart = Math.max(0, pulse.progress - CONFIG.pulseTrailLength);
          const gradient = ctx.createLinearGradient(
            sx + (ex - sx) * trailStart,
            sy + (ey - sy) * trailStart,
            px, py
          );
          gradient.addColorStop(0, `rgba(${pulse.color.r}, ${pulse.color.g}, ${pulse.color.b}, 0)`);
          gradient.addColorStop(1, `rgba(${pulse.color.r}, ${pulse.color.g}, ${pulse.color.b}, 0.5)`);
          
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(sx + (ex - sx) * trailStart, sy + (ey - sy) * trailStart);
          ctx.lineTo(px, py);
          ctx.stroke();
          
          // Draw pulse dot
          const pulseOpacity = 1 - Math.pow(pulse.progress, 2) * 0.5;
          
          // Outer glow
          ctx.beginPath();
          ctx.arc(px, py, CONFIG.pulseRadius * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${pulse.color.r}, ${pulse.color.g}, ${pulse.color.b}, ${pulseOpacity * 0.2})`;
          ctx.fill();
          
          // Inner glow
          ctx.beginPath();
          ctx.arc(px, py, CONFIG.pulseRadius * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${pulse.color.r}, ${pulse.color.g}, ${pulse.color.b}, ${pulseOpacity * 0.5})`;
          ctx.fill();
          
          // Core
          ctx.beginPath();
          ctx.arc(px, py, CONFIG.pulseRadius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${pulseOpacity})`;
          ctx.fill();
          
          // Draw ticker label if present
          if (pulse.ticker) {
            // Calculate ticker opacity based on progress (fade in/out)
            let tickerOpacity = CONFIG.tickerOpacityMax;
            if (pulse.progress < CONFIG.tickerFadeInEnd) {
              tickerOpacity *= pulse.progress / CONFIG.tickerFadeInEnd;
            } else if (pulse.progress > CONFIG.tickerFadeOutStart) {
              tickerOpacity *= (1 - pulse.progress) / (1 - CONFIG.tickerFadeOutStart);
            }
            
            // Brighten slightly at mid-travel
            const midBoost = 1 + 0.3 * Math.sin(pulse.progress * Math.PI);
            tickerOpacity *= midBoost;
            
            // Dim if in hero text area
            if (isInHeroArea(px, py, width, height)) {
              tickerOpacity *= CONFIG.heroAreaDimFactor;
            }
            
            const labelX = px + pulse.tickerOffsetX;
            const labelY = py + pulse.tickerOffsetY;
            
            // Draw ticker background pill
            const textWidth = ctx.measureText(pulse.ticker).width;
            const pillPadding = 4;
            const pillHeight = CONFIG.tickerFontSize + 4;
            
            ctx.fillStyle = `rgba(${pulse.color.r}, ${pulse.color.g}, ${pulse.color.b}, ${tickerOpacity * 0.15})`;
            ctx.beginPath();
            ctx.roundRect(
              labelX - textWidth / 2 - pillPadding,
              labelY - pillHeight / 2,
              textWidth + pillPadding * 2,
              pillHeight,
              pillHeight / 2
            );
            ctx.fill();
            
            // Draw ticker text
            ctx.fillStyle = `rgba(255, 255, 255, ${tickerOpacity})`;
            ctx.fillText(pulse.ticker, labelX, labelY);
          }
        }
      }

      // Draw nodes
      for (const node of nodes) {
        const nx = node.x + offsetX;
        const ny = node.y + offsetY;
        
        // Outer glow
        ctx.beginPath();
        ctx.arc(nx, ny, node.radius * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, ${node.brightness * 0.12})`;
        ctx.fill();
        
        // Inner glow
        ctx.beginPath();
        ctx.arc(nx, ny, node.radius * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, ${node.brightness * 0.35})`;
        ctx.fill();
        
        // Core
        ctx.beginPath();
        ctx.arc(nx, ny, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, ${node.brightness})`;
        ctx.fill();
      }
    };

    // Start animation or draw static frame
    if (prefersReducedMotion) {
      // Draw a single static frame with some static tickers
      requestAnimationFrame(() => {
        const nodes = nodesRef.current;
        ctx.clearRect(0, 0, width, height);
        
        // Draw connections
        for (const node of nodes) {
          for (const neighbor of node.neighbors) {
            const other = nodes[neighbor.index];
            const dist = neighbor.distance;
            const opacity = (1 - dist / CONFIG.connectionDistance) * CONFIG.connectionOpacityMultiplier;
            
            ctx.strokeStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        }
        
        // Draw nodes
        for (const node of nodes) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, ${node.brightness * 0.6})`;
          ctx.fill();
        }
        
        // Draw a few static ticker labels
        ctx.font = `600 ${CONFIG.tickerFontSize}px Inter, -apple-system, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const staticTickers = ['AAPL', 'NVDA', 'MSFT', 'GOOGL'];
        staticTickers.forEach((ticker, i) => {
          if (nodes.length > i * 3) {
            const node = nodes[i * 3];
            if (!isInHeroArea(node.x, node.y, width, height)) {
              ctx.fillStyle = `rgba(255, 255, 255, 0.25)`;
              ctx.fillText(ticker, node.x, node.y - 15);
            }
          }
        });
      });
    } else {
      animationRef.current = requestAnimationFrame(animate);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      observer.disconnect();
      canvas.parentElement?.removeEventListener('mousemove', handleMouseMove);
      canvas.parentElement?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [prefersReducedMotion, initNodes, updateNeighbors, createPulse, isInHeroArea]);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`} style={{ zIndex: 0 }}>
      {/* Subtle gradient backdrop */}
      <div 
        className="absolute inset-0 opacity-40"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, rgba(59, 130, 246, 0.05) 0%, transparent 60%)',
        }}
      />
      
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
        data-testid="constellation-canvas"
      />
      
      {/* Faint watermark labels */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        <span 
          className="absolute text-[100px] font-bold tracking-wider hidden md:block"
          style={{
            color: 'rgba(59, 130, 246, 0.012)',
            top: '12%',
            left: '-2%',
            transform: 'rotate(-8deg)',
            fontFamily: 'Inter, sans-serif',
            whiteSpace: 'nowrap',
          }}
        >
          S&P 500
        </span>
        <span 
          className="absolute text-[70px] font-semibold tracking-wide hidden md:block"
          style={{
            color: 'rgba(0, 229, 255, 0.01)',
            bottom: '18%',
            right: '-3%',
            transform: 'rotate(5deg)',
            fontFamily: 'Inter, sans-serif',
            whiteSpace: 'nowrap',
          }}
        >
          Signal Flow
        </span>
      </div>
    </div>
  );
};

export default MarketConstellationBackground;
